/* ============================================================
   Chimney Direct — filtering + sorting for the collection grid.

   <chimney-facets> wraps the section and swaps the results block via the
   Section Rendering API. Everything it listens for is delegated from the
   element itself, so the markup inside can be replaced wholesale without
   rebinding. Stock facets.js is not used: it is hard-wired to Dawn's
   #ProductGridContainer / menu-drawer markup, which this section does not have.
   ============================================================ */
(function () {
  'use strict';

  var TYPING_DEBOUNCE = 600;
  var CACHE_LIMIT = 20;

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var context = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  /* Price fields accept digits plus the separators money_without_currency emits. */
  function sanitizePrice(input) {
    var value = parseFloat(input.value.replace(/[^0-9.]/g, ''));
    if (isNaN(value)) {
      input.value = '';
      return;
    }
    var min = parseFloat(input.dataset.min);
    var max = parseFloat(input.dataset.max);
    if (!isNaN(min) && value < min) value = min;
    if (!isNaN(max) && value > max) value = max;
    input.value = String(value);
  }

  class ChimneyFacets extends HTMLElement {
    connectedCallback() {
      if (this.bound) {
        /* Re-attached (theme editor moves sections around): the delegated
           listeners rode along on the element, the window one did not. */
        window.addEventListener('popstate', this.onPopState);
        return;
      }
      this.bound = true;

      this.sectionId = this.dataset.sectionId;
      this.resultsId = 'ChimneyCollectionResults-' + this.sectionId;
      this.cache = new Map();
      this.controller = null;
      this.drawerOpen = false;
      this.drawerOpener = null;
      this.lastParams = window.location.search.replace(/^\?/, '');

      this.debouncedApply = debounce(
        function (form) {
          this.applyForm(form);
        }.bind(this),
        TYPING_DEBOUNCE
      );

      this.onPopState = this.onPopState.bind(this);

      this.addEventListener('input', this.onInput);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('submit', this.onSubmit);
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      if (this.controller) this.controller.abort();
      document.body.classList.remove('chimney-facets-open');
    }

    /* ---------------- Events ---------------- */

    onInput(event) {
      var input = event.target;
      if (!input.matches('[data-facet-price]')) return;
      var form = input.form;
      if (form) this.debouncedApply(form);
    }

    onChange(event) {
      var input = event.target;
      /* Price fields are already handled, debounced, on `input`. Letting the
         change event through as well would fire a second identical request. */
      if (input.matches('[data-facet-price]')) {
        sanitizePrice(input);
        return;
      }
      if (!input.matches('input[type="checkbox"], select')) return;
      var form = input.form;
      if (!form || !form.matches('[data-facet-form]')) return;
      this.applyForm(form);
    }

    onSubmit(event) {
      var form = event.target;
      if (!form.matches('[data-facet-form]')) return;
      event.preventDefault();
      this.applyForm(form);
    }

    onClick(event) {
      var remove = event.target.closest('[data-facet-remove]');
      if (remove) {
        event.preventDefault();
        var query = remove.href.indexOf('?') === -1 ? '' : remove.href.slice(remove.href.indexOf('?') + 1);
        this.render(query, true);
        return;
      }

      if (event.target.closest('[data-facet-drawer-open]')) {
        event.preventDefault();
        this.openDrawer();
        return;
      }

      if (event.target.closest('[data-facet-drawer-close]')) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      var showMore = event.target.closest('[data-facet-show-more]');
      if (showMore) {
        event.preventDefault();
        this.toggleShowMore(showMore);
      }
    }

    onKeydown(event) {
      if (!this.drawerOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawer();
        return;
      }
      if (event.key === 'Tab') this.trapFocus(event);
    }

    onPopState() {
      var params = window.location.search.replace(/^\?/, '');
      if (params === this.lastParams) return;
      this.render(params, false);
    }

    /* ---------------- Rendering ---------------- */

    applyForm(form) {
      var params = new URLSearchParams();
      new FormData(form).forEach(function (value, key) {
        var trimmed = typeof value === 'string' ? value.trim() : value;
        /* Empty price fields would otherwise leave `filter.v.price.gte=` in the URL. */
        if (trimmed !== '') params.append(key, trimmed);
      });
      this.render(params.toString(), true);
    }

    render(searchParams, pushState) {
      var section = this;
      var url =
        window.location.pathname +
        '?section_id=' +
        encodeURIComponent(this.sectionId) +
        (searchParams ? '&' + searchParams : '');

      this.setLoading(true);

      /* Always drop the in-flight request: a slower earlier response must not
         land on top of the selection the visitor has just made. */
      if (this.controller) this.controller.abort();

      var cached = this.cache.get(url);
      if (cached) {
        this.finish(cached, searchParams, pushState);
        return;
      }

      this.controller = new AbortController();

      fetch(url, { signal: this.controller.signal })
        .then(function (response) {
          if (!response.ok) throw new Error('Filter request failed: ' + response.status);
          return response.text();
        })
        .then(function (html) {
          if (section.cache.size >= CACHE_LIMIT) section.cache.clear();
          section.cache.set(url, html);
          section.finish(html, searchParams, pushState);
        })
        .catch(function (error) {
          if (error.name === 'AbortError') return;
          console.error(error);
          /* Never strand the visitor mid-filter: fall back to a full page load. */
          window.location.search = searchParams;
        });
    }

    finish(html, searchParams, pushState) {
      this.swap(html);
      this.setLoading(false);
      this.lastParams = searchParams;
      if (pushState) {
        var target = window.location.pathname + (searchParams ? '?' + searchParams : '');
        history.pushState({ chimneyFacets: searchParams }, '', target);
      }
    }

    swap(html) {
      var current = document.getElementById(this.resultsId);
      var next = new DOMParser().parseFromString(html, 'text/html').getElementById(this.resultsId);
      if (!current || !next) return;

      var state = this.captureState(current);
      current.innerHTML = next.innerHTML;
      this.restoreState(current, state);
      this.syncDrawer();
      this.announce(current);

      document.dispatchEvent(
        new CustomEvent('chimney:facets:rendered', { detail: { sectionId: this.sectionId } })
      );
    }

    announce(container) {
      var live = this.querySelector('[data-facet-live]');
      var source = container.querySelector('[data-facet-announcement]');
      if (live && source) live.textContent = source.textContent.trim();
    }

    /* Disclosure/show-more/focus state lives only in the DOM, so it has to be
       carried across the swap by hand or every filter click collapses the panel. */
    captureState(container) {
      var open = [];
      var known = [];
      var expanded = [];

      container.querySelectorAll('[data-facet-key]').forEach(function (details) {
        known.push(details.dataset.facetKey);
        if (details.open) open.push(details.dataset.facetKey);
      });
      container.querySelectorAll('[data-facet-show-more][aria-expanded="true"]').forEach(function (button) {
        var group = button.closest('[data-facet-key]');
        if (group) expanded.push(group.dataset.facetKey);
      });

      var active = document.activeElement;
      var focus = null;
      if (active && active.id && container.contains(active)) {
        focus = { id: active.id, start: null, end: null };
        if (active.matches('[data-facet-price]')) {
          focus.start = active.selectionStart;
          focus.end = active.selectionEnd;
        }
      }

      return { open: open, known: known, expanded: expanded, focus: focus };
    }

    restoreState(container, state) {
      container.querySelectorAll('[data-facet-key]').forEach(function (details) {
        var key = details.dataset.facetKey;
        /* Filters that were not on the page before keep the server's default. */
        if (state.known.indexOf(key) === -1) return;
        details.open = state.open.indexOf(key) !== -1;
      });

      var self = this;
      container.querySelectorAll('[data-facet-show-more]').forEach(function (button) {
        var group = button.closest('[data-facet-key]');
        if (group && state.expanded.indexOf(group.dataset.facetKey) !== -1) self.toggleShowMore(button, true);
      });

      if (!state.focus) return;
      var restored = container.querySelector('#' + CSS.escape(state.focus.id));
      if (!restored) return;
      restored.focus({ preventScroll: true });
      if (state.focus.start !== null && restored.setSelectionRange) {
        try {
          restored.setSelectionRange(state.focus.start, state.focus.end);
        } catch (error) {
          /* Not all input types support selection ranges; nothing to recover. */
        }
      }
    }

    setLoading(isLoading) {
      var results = document.getElementById(this.resultsId);
      if (!results) return;
      results.classList.toggle('is-loading', isLoading);
      results.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    /* ---------------- Show more ---------------- */

    toggleShowMore(button, forceExpand) {
      var group = button.closest('[data-facet-key]');
      if (!group) return;
      var expand = forceExpand === true ? true : button.getAttribute('aria-expanded') !== 'true';
      group.querySelectorAll('[data-facet-more-item]').forEach(function (item) {
        item.hidden = !expand;
      });
      button.setAttribute('aria-expanded', String(expand));
    }

    /* ---------------- Drawer ---------------- */

    openDrawer() {
      var drawer = this.querySelector('[data-facet-drawer]');
      if (!drawer) return;
      this.drawerOpener = document.activeElement;
      this.drawerOpen = true;
      this.syncDrawer();
      var close = drawer.querySelector('[data-facet-drawer-close]:not([tabindex="-1"])');
      if (close) close.focus();
    }

    closeDrawer() {
      if (!this.drawerOpen) return;
      this.drawerOpen = false;
      this.syncDrawer();
      if (this.drawerOpener && document.contains(this.drawerOpener)) {
        this.drawerOpener.focus();
      } else {
        var opener = this.querySelector('[data-facet-drawer-open]');
        if (opener) opener.focus();
      }
      this.drawerOpener = null;
    }

    /* Re-applied after every swap, because the drawer markup is inside the
       replaced results block and comes back from the server closed. */
    syncDrawer() {
      var drawer = this.querySelector('[data-facet-drawer]');
      if (!drawer) return;
      drawer.classList.toggle('is-open', this.drawerOpen);
      if (this.drawerOpen) {
        drawer.removeAttribute('inert');
      } else {
        drawer.setAttribute('inert', '');
      }
      this.querySelectorAll('[data-facet-drawer-open]').forEach(
        function (opener) {
          opener.setAttribute('aria-expanded', String(this.drawerOpen));
        }.bind(this)
      );
      document.body.classList.toggle('chimney-facets-open', this.drawerOpen);
    }

    trapFocus(event) {
      var drawer = this.querySelector('[data-facet-drawer]');
      if (!drawer) return;
      var focusable = Array.prototype.filter.call(
        drawer.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
        ),
        function (element) {
          return element.offsetWidth > 0 || element.offsetHeight > 0;
        }
      );
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('chimney-facets')) {
    customElements.define('chimney-facets', ChimneyFacets);
  }
})();
